import { msg } from '@lit/localize';
import type { Items } from 'rete-context-menu-plugin/_types/types';
import { api } from '../../../../api';
import type { Id } from '../../../../api/types';
import type { Schemes } from './plugins';
import { TaskGroupNode } from './task-group-node';
import type { TaskNode } from './task-node';

export type ClipboardCommand = 'copy' | 'cut' | 'paste';

export type ClipboardTarget =
  | { kind: 'root' }
  | { kind: 'group' }
  | { kind: 'annotation' }
  | { kind: 'task'; taskId: number };

interface Options {
  container: HTMLElement;
  getWorkflowId: () => Id | undefined;
  deleteTaskNode: (node: TaskNode) => Promise<void>;
  resolveClipboardTarget: (context: unknown) => ClipboardTarget;
  canRunClipboardCommand: (
    command: ClipboardCommand,
    target: ClipboardTarget
  ) => boolean;
  runClipboardCommand: (
    command: ClipboardCommand,
    target: ClipboardTarget
  ) => Promise<void>;
  addTextAnnotation?: () => void;
  addRectAnnotation?: () => void;
}

export function createContextMenuItems({
  container,
  getWorkflowId,
  deleteTaskNode,
  resolveClipboardTarget,
  canRunClipboardCommand,
  runClipboardCommand,
  addTextAnnotation,
  addRectAnnotation,
}: Options): Items<Schemes> {
  const isGroupContext = (
    context: unknown
  ): context is { groupKey: string | number } =>
    typeof context === 'object' && context !== null && 'groupKey' in context;

  const clipboardItems = (target: ClipboardTarget) =>
    (
      [
        ['copy', msg('Copy')],
        ['cut', msg('Cut')],
        ['paste', msg('Paste')],
      ] as const
    )
      .filter(([command]) => canRunClipboardCommand(command, target))
      .map(([command, label]) => ({
        key: command,
        label,
        handler: async () => {
          await runClipboardCommand(command, target);
        },
      }));

  return (context) => {
    const clipboardTarget = resolveClipboardTarget(context);
    if (context instanceof TaskGroupNode || isGroupContext(context)) {
      const groupNode = context instanceof TaskGroupNode ? context : undefined;
      return {
        searchBar: false,
        list: [
          ...clipboardItems(clipboardTarget),
          {
            key: 'toggle-visibility',
            label: groupNode?.contentVisible
              ? msg('Hide contents')
              : msg('Show contents'),
            handler: () => groupNode?.toggleContentVisibility?.(),
          },
          {
            key: 'ungroup',
            label: msg('Ungroup'),
            handler: () => {
              groupNode?.ungroup?.();
            },
          },
        ],
      };
    }

    if (context === 'root')
      return {
        searchBar: false,
        list: [
          ...(addTextAnnotation
            ? [
                {
                  key: 'add-annotation-text',
                  label: msg('Add note'),
                  handler: () => addTextAnnotation(),
                },
              ]
            : []),
          ...(addRectAnnotation
            ? [
                {
                  key: 'add-annotation-rect',
                  label: msg('Add rectangle'),
                  handler: () => addRectAnnotation(),
                },
              ]
            : []),
          ...clipboardItems(clipboardTarget),
          {
            key: 'layout',
            label: msg('Layout'),
            handler: async () => {
              container.dispatchEvent(
                new CustomEvent('arrange-layout', {
                  bubbles: true,
                  composed: true,
                })
              );
            },
          },
        ],
      };
    const node = context as TaskNode;
    const nodeView = node as TaskNode & { disabled?: boolean; apiId?: number };
    const apiId =
      typeof nodeView.apiId === 'number' ? nodeView.apiId : Number(node.id);
    const disabled = nodeView.disabled === true;
    return {
      searchBar: false,
      list: [
        ...clipboardItems(clipboardTarget),
        {
          key: 'delete',
          label: msg('Delete'),
          handler: async () => {
            await deleteTaskNode(node);
          },
        },
        {
          key: 'toggle-disabled',
          label: disabled ? msg('Enable') : msg('Disable'),
          handler: async () => {
            const wfId = getWorkflowId();
            if (!wfId) return;
            try {
              await api.workflows.updateTask(wfId, apiId, {
                state: { disabled: !disabled },
              });
              container.dispatchEvent(
                new CustomEvent('workflow-modified', {
                  bubbles: true,
                  composed: true,
                })
              );
            } catch (e) {
              console.error(e);
            }
          },
        },
      ],
    };
  };
}
